import { useEffect, useState } from 'react';
import { gql, useQuery } from '@apollo/client';
import { ProjectStatusesEnum } from '@thinknet-gmbh/platform-common';

import { useCurrentMemberContext } from 'src/contexts';
import { IActiveViewRange } from 'src/pages/gantt/gantt.interfaces';

export const GET_TIME_STRIPES = gql`
  query (
    $ganttId: ObjectId!
    $pagination: QueryOptionsInput!
    $search: SearchQueryInput
    $projectStatus: String
  ) {
    getTimeStripes(
      ganttId: $ganttId
      pagination: $pagination
      search: $search
      projectStatus: $projectStatus
    ) {
      collection {
        _id
        ganttId
        stripeName
        customer {
          name
          deleted
        }
        plannedHours
      }
      totalCount
    }
  }
`;

export const GET_ASSIGNED_TIME_STRIPES = gql`
  query ($ganttId: ObjectId!, $startDate: Date!, $endDate: Date!) {
    getAssignedTimeStripes(ganttId: $ganttId, startDate: $startDate, endDate: $endDate) {
      _id
      name
      projectId
      ganttId
      employeeId
      stripeName
      plannedHours
      confirmed
      flexible
      customer {
        name
        deleted
      }
      startDate
      endDate
    }
  }
`;

export const TIME_STRIPES_CHANGED_SUBSCRIPTION = gql`
  subscription timeStripesChanged($ganttId: ObjectId!, $startDate: Date, $endDate: Date) {
    timeStripesChanged(ganttId: $ganttId, startDate: $startDate, endDate: $endDate) {
      trigger
      timeStripe {
        _id
        name
        ganttId
        projectId
        employeeId
        stripeName
        plannedHours
        confirmed
        customer {
          name
          deleted
        }
      }
    }
  }
`;

export const useGetTimeStripes = (searchParam?: string) => {
  const [currentPage, setCurrentPage] = useState(1);
  const { ganttId } = useCurrentMemberContext();
  const [currentSort, setCurrentSort] = useState<{ field: string; sortBy: number }[]>([]);
  const pageSize = 20;
  const { data, loading, refetch, subscribeToMore } = useQuery(GET_TIME_STRIPES, {
    variables: {
      pagination: {
        ...(typeof searchParam === 'string' && { limit: pageSize }),
        skip: pageSize * currentPage - pageSize,
        sort: currentSort,
      },
      search: { searchParam },
      ganttId,
      projectStatus: ProjectStatusesEnum.ACTIVE,
    },
    skip: !ganttId,
    fetchPolicy: 'cache-and-network',
  });

  useEffect(() => {
    if (ganttId) {
      subscribeToMore({
        document: TIME_STRIPES_CHANGED_SUBSCRIPTION,
        variables: { ganttId },
        updateQuery: prev => {
          refetch();

          return prev;
        },
      });
    }
  }, [subscribeToMore, refetch, ganttId]);

  useEffect(() => setCurrentPage(1), [searchParam]);

  const { collection = [], totalCount = 0 } = data?.getTimeStripes || {};

  return {
    loading,
    timeStripes: collection,
    paginationConfig: {
      total: totalCount,
      pageSize,
      current: currentPage,
      onChange: (page: number) => setCurrentPage(page),
    },
    onTableChange: (a: any, b: any, { order }: any) => {
      if (order) {
        const sortBy = order === 'ascend' ? 1 : -1;

        setCurrentSort([{ field: 'plannedHours', sortBy }]);
      }
    },
  };
};

export const useGetAssignedTimeStripesForActiveGantt = ({
  startDate,
  endDate,
}: IActiveViewRange) => {
  const { ganttId } = useCurrentMemberContext();
  const { data, loading, subscribeToMore } = useQuery(GET_ASSIGNED_TIME_STRIPES, {
    variables: { ganttId, startDate, endDate },
    fetchPolicy: 'network-only',
  });

  useEffect(() => {
    if (ganttId) {
      subscribeToMore({
        document: TIME_STRIPES_CHANGED_SUBSCRIPTION,
        variables: { ganttId, startDate, endDate },
        updateQuery: (prev, { subscriptionData }) => {
          if (!subscriptionData.data) {
            return prev;
          }

          const { trigger, timeStripe } = subscriptionData.data?.timeStripesChanged || {};
          const timeStripes = prev?.getAssignedTimeStripes || [];
          const newTimeStripes = [...timeStripes];
          const findIndex = timeStripes.findIndex((value: any) => value._id === timeStripe._id);

          if (['timeStripeCreated', 'timeStripeUpdated'].includes(trigger) && findIndex < 0) {
            newTimeStripes.push(timeStripe);
          }

          if (findIndex > -1) {
            if (trigger === 'timeStripeRemoved') {
              newTimeStripes.splice(findIndex, 1);
            }

            if (trigger === 'timeStripeUpdated') {
              newTimeStripes[findIndex] = timeStripe;
            }
          }

          return { getAssignedTimeStripes: newTimeStripes };
        },
      });
    }
  }, [ganttId, subscribeToMore, startDate, endDate]);

  return { loading, timeStripes: data?.getAssignedTimeStripes || [] };
};
